// ─── Sprite Loader ────────────────────────────────────────────────────────────
// Reads manifest.json at runtime to support both built-in and custom sprites.
// Built-in sprites are rendered as SVG via sprites.ts.
// Custom sprites are PNG files in /public/sprites/custom/.

export interface CustomSpriteEntry {
  id: string;
  label: string;
  source: "custom";
  file: string;
  palette_count: number;
  palettes?: { key: string; label: string }[];
  /** Number of animation frames. */
  frame_count?: number;
  /** Milliseconds per frame. Default: 150. */
  frame_rate_ms?: number;
  /** Native pixel width of one frame. */
  frame_width?: number;
  /** Native pixel height of the sheet. */
  sheet_height?: number;
  /** Native total sheet width. */
  sheet_width?: number;
  /** Native x offset (px) for each frame in the source sheet. */
  frame_offsets?: number[];
}

export interface SpriteManifest {
  sprites: CustomSpriteEntry[];
}

// ─── Load manifest (server or client) ────────────────────────────────────────
export async function loadManifest(): Promise<SpriteManifest> {
  try {
    if (typeof window === "undefined") {
      const fs = await import("fs/promises");
      const path = await import("path");
      const filePath = path.join(process.cwd(), "public", "sprites", "manifest.json");
      const raw = await fs.readFile(filePath, "utf-8");
      return JSON.parse(raw) as SpriteManifest;
    } else {
      const res = await fetch("/sprites/manifest.json", { cache: "no-store" });
      return res.json() as Promise<SpriteManifest>;
    }
  } catch {
    return { sprites: [] };
  }
}

// ─── Get URL for a custom sprite ─────────────────────────────────────────────
export function customSpriteUrl(file: string, paletteIndex: number = 0): string {
  return `/sprites/custom/${file}?variant=${paletteIndex}`;
}

// ─── Check if a spriteClass is a custom sprite ───────────────────────────────
export function isCustomSprite(spriteClass: string): boolean {
  return spriteClass.startsWith("custom:");
}

// ─── Parse custom sprite ID from spriteClass ─────────────────────────────────
export function parseCustomSpriteId(spriteClass: string): string {
  return spriteClass.replace("custom:", "");
}

// ─── Build spriteClass string for custom sprite ───────────────────────────────
export function buildCustomSpriteClass(id: string): string {
  return `custom:${id}`;
}

// ─── Compute background-position style for a single animation frame ──────────
//
// HOW THIS WORKS
// ──────────────
// We scale the sprite so that its native height fills `displayHeight` pixels.
// The div width is set to one scaled frame width (not displayHeight — sprites
// are usually taller than they are wide, or asymmetric).
//
// Scale factor = displayHeight / nativeSheetHeight
// Scaled frame width = nativeFrameWidth * scale   → this is the div width
// Scaled sheet width = nativeSheetWidth * scale   → background-size x
// Scaled frame x    = nativeFrameOffset * scale   → background-position x (negated)
//
// The caller wraps this in a container sized to displayHeight × displayHeight
// with overflow:hidden and centering if they want a square slot.
//
export function spriteFrameStyle(
  entry: CustomSpriteEntry,
  frameIndex: number,
  displayHeight: number,
): {
  style: React.CSSProperties;
  scaledFrameWidth: number;
} {
  const nativeFrameWidth = entry.frame_width ?? (entry.sheet_width ?? displayHeight);
  const nativeSheetHeight = entry.sheet_height ?? displayHeight;
  const nativeSheetWidth = entry.sheet_width ?? displayHeight;
  const offsets = entry.frame_offsets;
  const nativeOffsetX = offsets ? (offsets[frameIndex] ?? 0) : frameIndex * nativeFrameWidth;

  const scale = displayHeight / nativeSheetHeight;
  const scaledSheetWidth = Math.round(nativeSheetWidth * scale);
  const scaledFrameWidth = Math.round(nativeFrameWidth * scale);
  const scaledOffsetX = Math.round(nativeOffsetX * scale);

  const style: React.CSSProperties = {
    width: scaledFrameWidth,
    height: displayHeight,
    backgroundImage: `url(/sprites/custom/${entry.file})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: `${scaledSheetWidth}px ${displayHeight}px`,
    backgroundPosition: `-${scaledOffsetX}px 0px`,
    imageRendering: "pixelated" as const,
    flexShrink: 0,
  };

  return { style, scaledFrameWidth };
}
